/**
 * Phase B — Centralized WhatsApp Contact Resolver
 *
 * Resolves the WhatsApp number for a given purpose, honoring per-tenant
 * overrides when present, otherwise falling back to the platform-wide
 * `platform_whatsapp_contacts` entry (and finally the 'central' entry).
 *
 * Usage:
 *   const phone = await resolveWhatsAppNumber('orders_cta', tenantId);
 *   const url = buildWhatsAppLink(phone, 'Hello');
 */
import { supabase } from "@/integrations/supabase/client";
import { useTenantStore } from "@/lib/tenantContext";
import { useEffect, useState } from "react";

export type WhatsAppPurpose =
  | "central"
  | "flash_sale"
  | "need_help"
  | "live_chat"
  | "orders_cta"
  | "support"
  | "inquiry"
  | "swap";

interface CachedResolve { at: number; phone: string | null; }
const cache = new Map<string, CachedResolve>();
const TTL = 60_000;

function cacheKey(tenantId: string | null, purpose: WhatsAppPurpose) {
  return `${tenantId || "_global"}::${purpose}`;
}

export async function resolveWhatsAppNumber(
  purpose: WhatsAppPurpose,
  tenantId: string | null = null,
): Promise<string | null> {
  const k = cacheKey(tenantId, purpose);
  const c = cache.get(k);
  if (c && Date.now() - c.at < TTL) return c.phone;
  try {
    const { data, error } = await (supabase.rpc as any)("resolve_whatsapp_contact", {
      _tenant_id: tenantId, _purpose: purpose,
    });
    if (error) {
      console.warn("[whatsapp] resolve failed", error);
      cache.set(k, { at: Date.now(), phone: null });
      return null;
    }
    const phone = (data as string | null) ?? null;
    cache.set(k, { at: Date.now(), phone });
    return phone;
  } catch (e) {
    console.warn("[whatsapp] resolve threw", e);
    return null;
  }
}

export function invalidateWhatsAppCache() { cache.clear(); }

export function normalizePhoneForWa(raw: string | null | undefined): string {
  if (!raw) return "";
  const digits = raw.replace(/\D+/g, "");
  // Strip leading 0 for Ghana numbers (assume +233)
  if (digits.startsWith("00")) return digits.slice(2);
  if (digits.startsWith("0") && digits.length === 10) return `233${digits.slice(1)}`;
  return digits;
}

export function buildWhatsAppLink(phone: string | null | undefined, message?: string): string {
  const num = normalizePhoneForWa(phone || "");
  if (!num) return "#";
  const base = `https://wa.me/${num}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

/** React hook that auto-resolves and re-resolves when tenant scope changes. */
export function useWhatsAppNumber(purpose: WhatsAppPurpose): string | null {
  const tenantId = useTenantStore(s => s.tenantId);
  const [num, setNum] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    void resolveWhatsAppNumber(purpose, tenantId).then((p) => { if (alive) setNum(p); });
    return () => { alive = false; };
  }, [tenantId, purpose]);
  return num;
}

/**
 * Resolves a WhatsApp number for the active tenant scope (if any),
 * falling back to a caller-provided default (typically the existing
 * admin-settings number used by the main site).
 *
 * This is the recommended hook for storefront CTAs — it lets the same
 * component work on the main site and inside a tenant scope without
 * any conditional logic at the call site.
 *
 * The returned value is always normalised to digits (ready to drop into
 * `https://wa.me/${num}`).
 */
export function useResolvedWhatsApp(purpose: WhatsAppPurpose, fallback: string | null | undefined): string {
  const tenantId = useTenantStore(s => s.tenantId);
  const [resolved, setResolved] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    void resolveWhatsAppNumber(purpose, tenantId).then((p) => { if (alive) setResolved(p); });
    return () => { alive = false; };
  }, [tenantId, purpose]);
  return normalizePhoneForWa(resolved || fallback || "");
}

/**
 * PHASE C — Log every storefront WhatsApp dispatch to comm_routing_audit
 * so platform admins have full oversight of who clicked what, on which
 * tenant, from which source page. Fire-and-forget; never blocks the click.
 */
export function logWhatsAppDispatch(opts: {
  purpose: WhatsAppPurpose;
  tenantId?: string | null;
  resolvedTo?: string | null;
  source?: string | null;
  customerRef?: string | null;
  metadata?: Record<string, unknown>;
}) {
  try {
    void (supabase.from as any)("comm_routing_audit").insert({
      channel: "whatsapp",
      purpose: opts.purpose,
      tenant_id: opts.tenantId ?? null,
      customer_ref: opts.customerRef ?? null,
      source: opts.source ?? (typeof window !== "undefined" ? window.location.pathname : null),
      resolved_to: opts.resolvedTo ?? null,
      metadata: opts.metadata ?? {},
    });
  } catch { /* noop */ }
}

/** Convenience: build a wa.me link AND log the dispatch in one call. */
export function openRoutedWhatsApp(opts: {
  purpose: WhatsAppPurpose;
  phone: string | null | undefined;
  message?: string;
  tenantId?: string | null;
  source?: string | null;
  customerRef?: string | null;
  metadata?: Record<string, unknown>;
}): string {
  const url = buildWhatsAppLink(opts.phone, opts.message);
  logWhatsAppDispatch({
    purpose: opts.purpose,
    tenantId: opts.tenantId,
    resolvedTo: opts.phone ?? null,
    source: opts.source,
    customerRef: opts.customerRef,
    metadata: { ...(opts.metadata || {}), hasMessage: Boolean(opts.message) },
  });
  return url;
}

/**
 * React hook returning a single function `(message: string) => void` that:
 *   1. resolves the tenant-aware WhatsApp number for the given purpose
 *      (falling back to the supplied default), and
 *   2. logs the dispatch to `comm_routing_audit`, and
 *   3. opens the wa.me link (popup-blocker-safe).
 *
 * Standardises every storefront WhatsApp CTA through the Phase C
 * routing/audit layer.
 */
export function useRoutedWhatsAppOpener(
  purpose: WhatsAppPurpose,
  fallback: string | null | undefined,
  source?: string | null,
) {
  const tenantId = useTenantStore(s => s.tenantId);
  const phone = useResolvedWhatsApp(purpose, fallback);
  return (message: string, extra?: { customerRef?: string | null; metadata?: Record<string, unknown> }) => {
    const url = openRoutedWhatsApp({
      purpose, phone, message, tenantId,
      source: source ?? (typeof window !== "undefined" ? window.location.pathname : null),
      customerRef: extra?.customerRef,
      metadata: extra?.metadata,
    });
    const win = typeof window !== "undefined" ? window.open(url, "_blank") : null;
    if (!win && typeof window !== "undefined") window.location.href = url;
    return url;
  };
}



